import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Wand2, Loader2, Sparkles, Globe, ArrowRight, Clock, CheckCircle2,
  ImageIcon, Info, Mail, X, Send,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _authHeaders = () => {
  const t = localStorage.getItem("wt_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STEP_LABEL = {
  concept: "Pick a concept",
  posts:   "Refine posts",
  review:  "Approve posts",
  done:    "Exported",
};

const STEP_PROGRESS = {
  concept: 40,
  posts:   60,
  review:  80,
  done:   100,
};

export default function MyCampaigns() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState("all");     // all | awaiting | drafts | approved
  const [showDigest, setShowDigest] = useState(false);

  const fetchRows = async (mode) => {
    setLoading(true);
    try {
      const url = mode === "awaiting"
        ? `${API}/visual-studio/campaigns/awaiting-me`
        : `${API}/visual-studio/campaigns?mine=true`;
      const [r, u] = await Promise.all([
        axios.get(url, { headers: _authHeaders() }),
        axios.get(`${API}/visual-studio/auto-draft/unread`,
                     { headers: _authHeaders() }).catch(() => ({ data: { unread: 0 } })),
      ]);
      let list = r.data.campaigns || [];
      if (mode === "drafts")   list = list.filter(c => c.is_auto_draft);
      if (mode === "approved") list = list.filter(c => (c.approved_count || 0) > 0);
      setRows(list);
      setUnread(u.data.unread || 0);
      setErr(null);
    } catch (e) {
      if (e?.response?.status === 401) setErr("Please sign in to see your campaigns");
      else setErr("Couldn't load campaigns");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchRows(filter); /* eslint-disable-next-line */ }, [filter]);

  const resume = async (cid) => {
    // Mark auto-drafts as viewed
    const row = rows.find(r => r.id === cid);
    if (row?.is_auto_draft && !row.auto_draft_viewed) {
      axios.post(`${API}/visual-studio/auto-draft/mark-viewed`,
                     { campaign_ids: [cid] },
                     { headers: _authHeaders() }).catch(() => {});
    }
    nav(`/ad-builder?campaign=${cid}`);
  };

  return (
    <div className="min-h-screen bg-[#0b1120] text-slate-100" data-testid="my-campaigns-page">
      <header className="border-b border-white/5 bg-[#0b1120]/95 sticky top-0 z-30 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-slate-100 hover:text-white transition">
            <Wand2 className="w-5 h-5 text-fuchsia-400" />
            <span className="font-semibold tracking-tight">Visual Studio · My Campaigns</span>
          </a>
          <div className="flex items-center gap-3 text-sm">
            <button onClick={() => setShowDigest(true)}
                     data-testid="mc-digest-preview-btn"
                     className="text-slate-400 hover:text-slate-200 flex items-center gap-1.5">
              <Mail className="w-4 h-4" /> Preview digest
            </button>
            <a href="/ad-builder" className="text-slate-400 hover:text-slate-200"
                data-testid="mc-new-campaign-link">
              Start new campaign →
            </a>
          </div>
        </div>
      </header>

      {showDigest && (
        <DigestPreviewModal onClose={() => setShowDigest(false)} />
      )}

      <main className="max-w-6xl mx-auto px-6 py-10">
        {loading && (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
        {!loading && err && (
          <div className="text-center py-20 space-y-4" data-testid="mc-empty">
            <Info className="w-8 h-8 text-slate-500 mx-auto" />
            <p className="text-slate-400">{err}</p>
            {err.includes("sign in") && (
              <a href="/login" className="inline-block px-4 py-2 rounded-lg
                                            bg-fuchsia-500/20 text-fuchsia-200
                                            hover:bg-fuchsia-500/30">
                Sign in
              </a>
            )}
          </div>
        )}
        {!loading && !err && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h1 className="text-2xl font-semibold flex items-center gap-3">
                Your campaigns
                {unread > 0 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full
                                     bg-emerald-500/20 text-emerald-200 text-xs font-medium"
                        data-testid="mc-unread-badge">
                    <Sparkles className="w-3 h-3" />
                    {unread} new draft{unread === 1 ? "" : "s"}
                  </span>
                )}
              </h1>
              <div className="text-xs text-slate-500">{rows.length} showing</div>
            </div>

            {/* Filter chips */}
            <div className="flex gap-2 flex-wrap mb-4">
              {[
                { key: "all",       label: "All"          },
                { key: "awaiting",  label: "Awaiting me"  },
                { key: "drafts",    label: "My drafts"    },
                { key: "approved",  label: "Approved"     },
              ].map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)}
                          data-testid={`mc-filter-${f.key}`}
                          className={`px-3 py-1.5 rounded-full text-xs transition
                              ${filter === f.key
                                    ? "bg-fuchsia-500/20 text-fuchsia-200 ring-1 ring-fuchsia-500/40"
                                    : "bg-white/5 text-slate-400 hover:bg-white/10"}`}>
                  {f.label}
                </button>
              ))}
            </div>

            {rows.length === 0 ? (
              <div className="text-center py-10 text-slate-500 text-sm"
                     data-testid="mc-filter-empty">
                Nothing matches this filter.
              </div>
            ) : (
              rows.map(r => (
                <CampaignRow key={r.id} row={r} onResume={() => resume(r.id)} />
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function CampaignRow({ row, onResume }) {
  const step = row.current_step || "concept";
  const progress = STEP_PROGRESS[step] ?? 40;
  const done = step === "done";
  const isNewDraft = row.is_auto_draft && !row.auto_draft_viewed;
  const updated = new Date(row.updated_at || row.created_at);
  const ago = timeAgo(updated);
  return (
    <button onClick={onResume}
      data-testid={`mc-row-${row.id}`}
      className={`w-full text-left bg-white/[0.03] border rounded-2xl p-5
                  hover:border-fuchsia-500/60 hover:bg-white/[0.05] transition group
                  ${isNewDraft ? "border-emerald-500/40 ring-1 ring-emerald-500/20"
                                    : "border-white/10"}`}>
      <div className="flex items-center gap-5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Globe className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-xs text-slate-500 font-mono truncate">{row.domain || "—"}</span>
            {isNewDraft && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-200 font-medium">
                NEW · auto-draft
              </span>
            )}
          </div>
          <h3 className="font-semibold truncate">{row.brand_name || "Untitled brand"}</h3>
          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
            <span>{row.objective_label}</span>
            <span>·</span>
            <span className="capitalize">{row.selected_concept || "no concept yet"}</span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> {ago}
            </span>
          </div>
        </div>
        <div className="w-40 flex-shrink-0">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 flex justify-between">
            <span>{STEP_LABEL[step]}</span>
            <span>{row.approved_count}/{row.post_count}</span>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div className={`h-full transition-all ${done ? "bg-emerald-500" : "bg-fuchsia-500"}`}
                 style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div className={`flex items-center gap-1 text-sm font-medium
                     ${done ? "text-emerald-300" : "text-fuchsia-300"} group-hover:gap-2 transition-all`}>
          {done ? <><CheckCircle2 className="w-4 h-4" /> Reopen</>
                    : <>Resume <ArrowRight className="w-4 h-4" /></>}
        </div>
      </div>
    </button>
  );
}

function timeAgo(d) {
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function DigestPreviewModal({ onClose }) {
  const [state, setState] = useState({ loading: true });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/visual-studio/auto-draft/digest-preview`,
          { headers: _authHeaders() });
        setState({ loading: false, data });
      } catch (err) {
        setState({ loading: false,
                     err: err?.response?.data?.detail || "Preview failed" });
      }
    })();
  }, []);

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/visual-studio/auto-draft/send-test-digest`,
        {}, { headers: _authHeaders() });
      if (data.sent) toast.success(`Test sent to ${data.to}`);
      else toast.info(data.reason || "Nothing to send");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4
                       bg-black/70 backdrop-blur-sm"
          onClick={onClose} data-testid="mc-digest-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl
                          max-w-2xl w-full max-h-[85vh] overflow-hidden
                          flex flex-col"
              onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Mail className="w-4 h-4 text-fuchsia-300" /> Monday digest preview
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              This is what lands in your inbox every Monday morning.
            </p>
          </div>
          <button onClick={onClose} data-testid="mc-digest-close"
                    className="text-slate-400 hover:text-slate-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {state.loading && (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}
          {state.err && (
            <div className="p-8 text-center text-red-300 text-sm">{state.err}</div>
          )}
          {state.data && state.data.count === 0 && (
            <div className="p-12 text-center text-slate-400" data-testid="mc-digest-empty">
              <Mail className="w-10 h-10 text-slate-600 mx-auto mb-4" />
              <p className="mb-1">No unread drafts to summarise.</p>
              <p className="text-xs text-slate-500">
                Enable auto-draft on a Brand to receive a Monday digest.
              </p>
            </div>
          )}
          {state.data && state.data.count > 0 && (
            <>
              <div className="px-5 py-3 bg-black/40 border-b border-white/5">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">Subject</div>
                <div className="text-sm text-slate-200 mt-1"
                       data-testid="mc-digest-subject">{state.data.subject}</div>
              </div>
              <div className="bg-white">
                <iframe title="digest-preview"
                          srcDoc={state.data.html}
                          data-testid="mc-digest-iframe"
                          className="w-full h-[500px] border-0" />
              </div>
            </>
          )}
        </div>

        {state.data && state.data.count > 0 && (
          <div className="p-4 border-t border-white/10 flex justify-end gap-2">
            <button onClick={onClose}
                     className="text-xs px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300">
              Close
            </button>
            <button onClick={sendTest} disabled={sending}
                     data-testid="mc-digest-send-test"
                     className="text-xs px-4 py-2 rounded-lg bg-fuchsia-500/20 text-fuchsia-200
                                 hover:bg-fuchsia-500/30 flex items-center gap-1.5 disabled:opacity-50">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Send className="w-3.5 h-3.5" />}
              Send test to my inbox
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
